/**
 * siteMapBounds — pure helpers for the Overview map's fit-to-bounds
 * behaviour (Reader Polish Prompt 4).
 *
 * `computeSiteMapBounds` returns the union LatLngBounds-equivalent
 * rectangle covering the site boundary plus every layer's geometry.
 * Returns null when there's nothing to fit (e.g. point-only sites
 * with no layers — the caller falls back to the existing
 * defaultCenter / defaultZoom logic).
 *
 * Pure / Google-Maps-free so the calculation is testable without
 * mounting the maps SDK.
 */
import type { SiteLayer } from '../types/site';

export interface LatLng { lat: number; lng: number }
export interface BoundsRect {
  /** SW corner (min lat, min lng). */
  south: number;
  west: number;
  /** NE corner (max lat, max lng). */
  north: number;
  east: number;
}

interface SiteShape {
  centroid?: LatLng | null;
  boundary?: LatLng[];
  layers?: SiteLayer[];
}

export interface ComputeSiteMapBoundsOptions {
  /**
   * Exclude `line`-type layer geometries (pipelines / transmission lines)
   * from the fit bbox. A long pipeline tail otherwise drags the union
   * bounds out to the whole region and the parcels shrink to a dot. The
   * lines still RENDER as overlays — they just don't drive the zoom.
   * Defaults to false (legacy behaviour: every visible layer contributes).
   */
  excludeLineLayers?: boolean;
}

export function computeSiteMapBounds(
  site: SiteShape,
  options: ComputeSiteMapBoundsOptions = {},
): BoundsRect | null {
  const { excludeLineLayers = false } = options;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  let minLng = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  let any = false;

  function include(p: LatLng | undefined | null) {
    if (!p) return;
    if (typeof p.lat !== 'number' || typeof p.lng !== 'number') return;
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
    any = true;
  }

  if (site.boundary && site.boundary.length > 0) {
    for (const p of site.boundary) include(p);
  }

  if (site.layers && site.layers.length > 0) {
    for (const layer of site.layers) {
      // Skip layers explicitly marked invisible — they don't influence
      // the initial-fit decision since the user can't see them.
      if (layer.visible === false) continue;
      // Pipelines / transmission lines are long, thin tails that would
      // otherwise blow the union bbox out to the whole region; exclude
      // them from the fit when asked (they still render as overlays).
      if (excludeLineLayers && layer.geometry?.type === 'line') continue;
      const coords = layer.geometry?.coordinates;
      if (!coords) continue;
      for (const p of coords) include(p);
    }
  }

  // Fall back to the centroid only when nothing else contributed.
  // The centroid alone produces a degenerate bounds (point), so the
  // caller still gets something to seed off but should rely on the
  // map's defaultZoom for the actual zoom level.
  if (!any && site.centroid) {
    include(site.centroid);
  }

  if (!any) return null;

  return { south: minLat, west: minLng, north: maxLat, east: maxLng };
}

/**
 * Pad a bounds rect outward by a fractional amount (default 10%).
 * Used so a site's outermost transmission line doesn't sit flush
 * against the map's right edge.
 */
export function padBounds(rect: BoundsRect, fraction = 0.1): BoundsRect {
  const latPad = Math.max((rect.north - rect.south) * fraction, 0.0005);
  const lngPad = Math.max((rect.east - rect.west) * fraction, 0.0005);
  return {
    south: rect.south - latPad,
    west:  rect.west  - lngPad,
    north: rect.north + latPad,
    east:  rect.east  + lngPad,
  };
}
